import {
  createUnifiedTheme,
  defaultTypography,
  palettes,
  shapes,
  type BackstageTypography,
  type PageTheme,
  type UnifiedThemeOptions,
} from '@backstage/theme';
import { renderCatalogFilterTags } from '../components/catalog/filters/renderCatalogFilterTags';

/** Bayer Sans first; Helvetica Neue until licensed woff2 files are added. */
export const BAYER_FONT_FAMILY =
  '"Bayer Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';

export const BAYER_CYAN = '#00BCFF';
export const BAYER_GREEN = '#1FBD81';
export const BAYER_LIME = '#8AD32A';
export const BAYER_NAVY = '#001F3D';
/** Accessible Bayer cyan for text and controls on white. */
export const BAYER_PRIMARY_LIGHT = '#0077A8';

export const bayerColors = [
  'rgb(0, 188, 255)',
  'rgb(31, 189, 129)',
  'rgb(138, 211, 42)',
];

// `shape` is included for @backstage/theme versions where PageTheme requires it (excess on older versions, ignored via the `as` cast below).
const bayerGradientShape = {
  colors: bayerColors,
  shape: shapes.wave,
  backgroundImage: `${shapes.wave}, linear-gradient(45deg, rgb(0, 188, 255) 35%, rgb(31, 189, 129) 75%, rgb(138, 211, 42) 100%)`,
  fontColor: BAYER_NAVY,
};
export const bayerGradient = bayerGradientShape as PageTheme;

/**
 * Page banners (Home, Create, Docs chrome) keep the Bayer wave gradient.
 *
 * `techdocs-page` / `api-docs-page` are legacy ids with no remaining users:
 * /docs and /api-docs now render CustomCatalogPage under the `home` id. They
 * are retained so any page or plugin still declaring them keeps the gradient
 * rather than falling back to the default banner.
 * They are intentionally NOT the same string as the scaffolder card ids
 * ("documentation" / "apis") below — those two strings are also common
 * `spec.type` values for scaffolder templates, and template cards on
 * /create must keep the solid-blue card look, not switch to this gradient.
 */
const BAYER_HEADER_THEME_IDS = [
  'home',
  'tool',
  'welcome',
  'help',
  'getting-started',
  'techdocs-page',
  'api-docs-page',
] as const;

/**
 * Scaffolder template cards key off spec.type (service, documentation, …).
 * Use a solid primary blue so card headers match the primary button, not the
 * Bayer rainbow used on full-page titles.
 */
const CARD_THEME_IDS = [
  'documentation',
  'service',
  'website',
  'library',
  'other',
  'app',
  'apis',
  'card',
] as const;

const bayerCardThemeShape = {
  colors: [BAYER_PRIMARY_LIGHT, BAYER_PRIMARY_LIGHT],
  shape: shapes.wave,
  backgroundImage: `${shapes.wave}, linear-gradient(90deg, ${BAYER_PRIMARY_LIGHT} 0%, ${BAYER_PRIMARY_LIGHT} 100%)`,
  fontColor: '#FFFFFF',
};
export const bayerCardTheme = bayerCardThemeShape as PageTheme;

export const bayerPageThemes: Record<string, PageTheme> = {
  ...Object.fromEntries(
    BAYER_HEADER_THEME_IDS.map(themeId => [themeId, bayerGradient]),
  ),
  ...Object.fromEntries(CARD_THEME_IDS.map(themeId => [themeId, bayerCardTheme])),
};

function headingStyle(
  heading: BackstageTypography['h1'],
): BackstageTypography['h1'] {
  return {
    ...heading,
    fontFamily: BAYER_FONT_FAMILY,
    fontWeight: 700,
  };
}

export const bayerTypography: BackstageTypography = {
  htmlFontSize: 16,
  fontFamily: BAYER_FONT_FAMILY,
  h1: headingStyle(defaultTypography.h1),
  h2: headingStyle(defaultTypography.h2),
  h3: headingStyle(defaultTypography.h3),
  h4: headingStyle(defaultTypography.h4),
  h5: headingStyle(defaultTypography.h5),
  h6: headingStyle(defaultTypography.h6),
};

const bayerBurstGradient = `linear-gradient(45deg, ${BAYER_CYAN} 0%, ${BAYER_GREEN} 50%, ${BAYER_LIME} 100%)`;

/**
 * The rounded top-left corner of the content area is owned by the shared
 * content frame in the app/layout override
 * (packages/app/src/modules/appRootModule.tsx). That frame wraps the routed
 * content for EVERY page — including ones that don't use the core `<Page>`
 * component — and is the single scroll container below the fixed top nav, so
 * the curve is identical everywhere and stays put on scroll.
 *
 * A rounded corner is only visible if a contrasting colour sits behind it, so
 * `BackstageSidebarPage` (the wrapper behind the content frame) is painted
 * navy — the notch reads as the content panel meeting the dark sidebar.
 *
 * Here we only make the core `<Page>` grid fill the frame (height: 100%)
 * instead of forcing its own 100vh, which would overflow the frame and create
 * a second scrollbar. The grid is left transparent so the content frame's
 * theme-aware background (white in light mode, dark in dark mode) shows
 * through.
 */
export const CONTENT_CORNER_RADIUS = 16;

const bayerContentComponents = {
  BackstageSidebarPage: {
    styleOverrides: {
      root: {
        backgroundColor: BAYER_NAVY,
      },
    },
  },
  // Catalog multi-selects (Owner, Tags, …) use CatalogAutocomplete → this name.
  // Never render per-value chips in the field; selection lives in the dropdown.
  BackstageAutocompleteBase: {
    defaultProps: {
      renderTags: renderCatalogFilterTags,
    },
  },
  // Labels show full values in the open list; Owner truncates with ellipsis by
  // default. Match Labels: keep the text on one line and let the listbox scroll
  // horizontally so long group names stay readable.
  MuiAutocomplete: {
    styleOverrides: {
      listbox: {
        overflowX: 'auto',
      },
    },
  },
  CatalogReactEntityOwnerPicker: {
    styleOverrides: {
      boxLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        '& .MuiTypography-root': {
          overflow: 'hidden !important',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      fullWidth: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
    },
  },
  FixedWidthFormControlLabel: {
    styleOverrides: {
      root: {
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      },
      label: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      // Header action icons that inherit their colour (e.g. SupportButton) go
      // navy here. The kebab menu's icon is locked by an inline color + a hashed
      // makeStyles class, so it's handled by a global <style> in LayoutWrapper
      // (see appRootModule.tsx) — UnifiedThemeProvider renders no CssBaseline,
      // so an MuiCssBaseline "@global" override would never be injected.
      // Outlined Guide buttons use color="inherit", which in dark mode inherits
      // body text (white); force navy to match the gradient header fontColor.
      header: {
        '& .MuiSvgIcon-root': {
          color: BAYER_NAVY,
          fill: BAYER_NAVY,
        },
        '& .MuiIconButton-root': {
          color: BAYER_NAVY,
        },
        '& .MuiButton-outlined': {
          color: BAYER_NAVY,
          borderColor: BAYER_NAVY,
          '&:hover': {
            borderColor: BAYER_NAVY,
            backgroundColor: 'rgba(0, 31, 61, 0.08)',
          },
        },
      },
    },
  },
  BackstagePage: {
    styleOverrides: {
      root: {
        height: '100%',
        backgroundColor: 'transparent',
      },
    },
  },
  // TechDocs Reader wraps content in `.techdocs-reader-page`. Tighten top
  // padding so the in-docs search bar sits closer to the tabs / subheader.
  BackstageContent: {
    styleOverrides: {
      root: {
        '.techdocs-reader-page &': {
          paddingTop: 8,
        },
      },
    },
  },
} as NonNullable<UnifiedThemeOptions['components']>;

export const bayerLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    primary: {
      main: BAYER_PRIMARY_LIGHT,
    },
    link: BAYER_PRIMARY_LIGHT,
    linkHover: BAYER_CYAN,
    navigation: {
      ...palettes.light.navigation,
      background: BAYER_NAVY,
      indicator: BAYER_LIME,
    },
    tabbar: {
      indicator: BAYER_LIME,
    },
    bursts: {
      ...palettes.light.bursts,
      gradient: {
        linear: bayerBurstGradient,
      },
    },
  },
  fontFamily: BAYER_FONT_FAMILY,
  typography: bayerTypography,
  // Unmapped page themeIds (e.g. scaffolder spec.types like adr/repository) fall
  // back to the solid-blue card theme, not the gradient, so cards stay uniform.
  defaultPageTheme: 'card',
  pageTheme: bayerPageThemes,
  components: bayerContentComponents,
});

export const bayerDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: BAYER_CYAN,
      dark: BAYER_PRIMARY_LIGHT,
    },
    link: BAYER_CYAN,
    linkHover: BAYER_GREEN,
    navigation: {
      ...palettes.dark.navigation,
      background: BAYER_NAVY,
      indicator: BAYER_LIME,
    },
    tabbar: {
      indicator: BAYER_LIME,
    },
    bursts: {
      ...palettes.dark.bursts,
      gradient: {
        linear: bayerBurstGradient,
      },
    },
  },
  fontFamily: BAYER_FONT_FAMILY,
  typography: bayerTypography,
  defaultPageTheme: 'card',
  pageTheme: bayerPageThemes,
  components: bayerContentComponents,
});
